import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { heroSlides as slides } from '../utils/images';

export default function Hero() {
  const [current, setCurrent] = useState(0);
  // Load the visible hero first; the next slide is added only after initial paint.
  const [loadedSlides, setLoadedSlides] = useState(() => new Set([0]));
  const { localizePath, t, translateValue } = useLanguage();
  const heroHighlights = t('hero.highlights');

  useEffect(() => {
    const timer = setInterval(() => setCurrent(c => (c + 1) % slides.length), 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setLoadedSlides((existing) => {
      const next = (current + 1) % slides.length;
      const updated = new Set(existing);
      updated.add(current);
      updated.add(next);
      return updated;
    });
  }, [current]);

  return (
    <section className="hero">
      <Link
        to={localizePath(slides[current].href || '/tours')}
        className="hero-slide-link"
        aria-label={t('hero.slideAria', {
          label: translateValue('heroSlideLabels', slides[current].label),
        })}
      />
      {slides.map((slide, i) => (
        <div
          key={`${i}-image`}
          className="hero-slide"
          aria-hidden="true"
          style={{ opacity: i === current ? 1 : 0 }}
        >
          {loadedSlides.has(i) && (
            <img
              className="hero-slide-image"
              src={slide.url}
              alt=""
              aria-hidden="true"
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchPriority={i === 0 ? 'high' : 'auto'}
              decoding="async"
            />
          )}
        </div>
      ))}
      <div className="hero-overlay" aria-hidden="true" />
      <div className="hero-content">
        <span className="hero-kicker">{t('hero.kicker')}</span>
        <h1>{t('hero.heading')}</h1>
        <p>{t('hero.body')}</p>
        <div className="hero-actions">
          <Link to={localizePath('/tours')} className="hero-btn">{t('hero.cta')}</Link>
          <Link to={localizePath('/transportation')} className="btn-outline hero-btn-secondary">
            {t('hero.secondaryCta')}
          </Link>
        </div>
        {Array.isArray(heroHighlights) && (
          <div className="hero-highlights">
            {heroHighlights.map((highlight) => (
              <span key={highlight} className="hero-highlight-pill">
                {highlight}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="hero-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`dot ${i === current ? 'active' : ''}`}
            aria-label={t('hero.dotAria', {
              label: translateValue('heroSlideLabels', slides[i].label),
            })}
            onClick={() => setCurrent(i)}
          />
        ))}
      </div>
      <Link to={localizePath(slides[current].href || '/tours')} className="hero-label">
        {translateValue('heroSlideLabels', slides[current].label)}
      </Link>
    </section>
  );
}
